/**
 * Authentication Context for Lab Platform
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { identifyClarityUser, tagClaritySession } from '../hooks/useClarityTracking';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check for existing token and validate it with backend
    const checkAuthState = async () => {
      try {
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('authUser');
        
        if (storedToken && storedUser) {
          // Set token first so it's included in the verify request
          apiService.setToken(storedToken);
          
          // Validate token with backend - with timeout to prevent infinite loading
          try {
            // Create a timeout promise
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Token verification timeout')), 10000)
            );
            
            // Race between verify and timeout
            const currentUser = await Promise.race([
              apiService.verifyToken(),
              timeoutPromise
            ]);
            
            setToken(storedToken);
            setUser(currentUser);
            setIsAuthenticated(true);
            // Tell Clarity who this returning user is so all their sessions
            // link together in the dashboard. Email is hashed inside the helper.
            if (currentUser?.email) {
              identifyClarityUser(currentUser.email, {
                user_type: 'student',
                source: 'returning',
              });
            }
          } catch (error) {
            clearAuthState();
          }
        } else {
          // No stored token
        }
      } catch (error) {
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    checkAuthState();
  }, []);

  const clearAuthState = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    apiService.setToken(null);
    
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  };

  const login = async (credentials) => {
    try {
      const response = await apiService.login(credentials);

      const { access_token, user: userData } = response;

      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      apiService.setToken(access_token);

      // Microsoft Clarity user identification — links all their sessions so
      // we can see the full pre- and post-login journey in one timeline.
      if (userData?.email) {
        identifyClarityUser(userData.email, {
          user_type: 'student',
          login_method: 'password',
        });
        tagClaritySession('event', 'login');
      }

      // Store in localStorage for persistence
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('authUser', JSON.stringify(userData));

      return response;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await apiService.register(userData);
      
      // Google Ads Conversion Tracking - Fire ONLY on successful signup
      // This fires after API confirms account creation (not on click/page load)
      if (response && response.access_token) {
        try {
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', {
              'send_to': 'AW-1002496102/gRxoCIWb7uwbEObAg94D',
              'value': 1.0,
              'currency': 'INR'
            });
            console.log('✅ Google Ads Conversion tracked: Successful Signup');
          }
        } catch (gtagError) {
          // Silently fail - don't break signup flow for tracking errors
          console.warn('⚠️ Google Ads tracking error (non-critical):', gtagError);
        }

        // Meta Pixel - Fire CompleteRegistration ONLY on confirmed success
        try {
          console.log('📊 Meta Pixel: attempting CompleteRegistration — fbq available:', typeof window.fbq === 'function');
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'CompleteRegistration', {
              currency: 'INR',
              value: 0,
            });
            console.log('✅ Meta Pixel CompleteRegistration tracked');
          } else {
            console.warn('⚠️ Meta Pixel: fbq not loaded — CompleteRegistration NOT sent');
          }
        } catch (fbqError) {
          console.warn('⚠️ Meta Pixel tracking error (non-critical):', fbqError);
        }

        // Microsoft Clarity — identify the new user + tag the session so we
        // can later filter "Meta-ad sign-ups" vs "organic sign-ups" in the
        // recordings dashboard.
        try {
          if (userData?.email) {
            identifyClarityUser(userData.email, {
              user_type: 'student',
              event: 'signup',
            });
            tagClaritySession('event', 'signup');
          }
        } catch (clarityErr) {
          console.warn('⚠️ Clarity tracking error (non-critical):', clarityErr);
        }
      }
      
      // Don't auto-login after registration
      // Just return the response so the component can show welcome message
      return response;
      
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAuthState();
  };

  /**
   * Merge partial updates into the current user object and persist to
   * localStorage. Used by ProfilePage after avatar upload so the Navbar
   * circle reactively switches to the photo without a full page reload.
   */
  const updateUser = (partial) => {
    setUser((prev) => {
      const updated = { ...prev, ...partial };
      localStorage.setItem('authUser', JSON.stringify(updated));
      return updated;
    });
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};